import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import ArticleCard from "@/components/ArticleCard";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Articles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const activeCategory = searchParams.get("category") || "";

  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, [activeCategory]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = activeCategory ? { category: activeCategory } : {};
      const response = await axios.get(`${API}/articles`, { params });
      setArticles(response.data);
    } catch (error) {
      console.error("Error fetching articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleCategoryChange = (category) => {
    if (category) {
      setSearchParams({ category });
    } else {
      setSearchParams({});
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  return (
    <div className="min-h-screen" data-testid="articles-page">
      <SEO
        title="Editorial"
        description="In-depth coverage of cinema, film festivals, and the global film industry from Filmcable."
        url="https://filmcable.net/articles"
      />
      
      {/* Mobile Header */}
      <section className="md:hidden py-8 text-center border-b border-zinc-200">
        <h1 className="font-serif text-4xl italic font-normal">
          Editorial
        </h1>
      </section>

      {/* Desktop Header */}
      <section className="hidden md:block py-16 md:py-24 border-b border-zinc-200">
        <div className="container">
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold mb-4">
            Editorial
          </h1>
          <div className="accent-line mb-6" />
          <p className="text-lg md:text-xl text-zinc-600 max-w-2xl">
            In-depth coverage of cinema, film festivals, and the global film industry.
          </p>
        </div>
      </section>

      {/* Mobile Category Tabs - Horizontal Scroll */}
      <section className="md:hidden border-b border-zinc-200 sticky top-16 bg-white z-30">
        <div className="overflow-x-auto">
          <div className="flex min-w-max">
            <button
              onClick={() => handleCategoryChange("")}
              className={`px-6 py-4 font-accent text-sm font-bold uppercase tracking-widest whitespace-nowrap border-b-2 transition-colors ${
                !activeCategory 
                  ? "border-[#FF66C4] text-[#FF66C4]" 
                  : "border-transparent text-zinc-400"
              }`}
              data-testid="filter-all-btn"
            >
              All Topics
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`px-6 py-4 font-accent text-sm font-bold uppercase tracking-widest whitespace-nowrap border-b-2 transition-colors ${
                  activeCategory === category 
                    ? "border-[#FF66C4] text-[#FF66C4]" 
                    : "border-transparent text-zinc-400"
                }`}
                data-testid={`filter-${category.toLowerCase()}-btn`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Desktop Filter Bar */}
      <section className="hidden md:block py-8 border-b border-zinc-200 sticky top-16 bg-white z-30">
        <div className="container">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => handleCategoryChange("")}
              className={`font-accent text-sm font-bold uppercase tracking-widest px-4 py-2 whitespace-nowrap transition-colors ${
                !activeCategory 
                  ? "bg-[#FF66C4] text-white" 
                  : "text-zinc-600 hover:text-zinc-900"
              }`}
              data-testid="filter-all-desktop-btn"
            >
              All
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`font-accent text-sm font-bold uppercase tracking-widest px-4 py-2 whitespace-nowrap transition-colors ${
                  activeCategory === category 
                    ? "bg-[#FF66C4] text-white" 
                    : "text-zinc-600 hover:text-zinc-900"
                }`}
                data-testid={`filter-${category.toLowerCase()}-desktop-btn`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="py-6 md:py-16 md:py-20">
        <div className="container">
          {loading ? (
            <>
              {/* Mobile Loading */}
              <div className="md:hidden space-y-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-32 h-40 skeleton flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-16 skeleton" />
                      <div className="h-6 w-full skeleton" />
                      <div className="h-4 w-24 skeleton" />
                      <div className="h-4 w-20 skeleton" />
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop Loading */}
              <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="space-y-4">
                    <div className="aspect-[3/4] skeleton" />
                    <div className="h-4 w-20 skeleton" />
                    <div className="h-8 w-full skeleton" />
                    <div className="h-4 w-24 skeleton" />
                    <div className="h-4 w-20 skeleton" />
                  </div>
                ))}
              </div>
            </>
          ) : articles.length > 0 ? (
            <>
              {/* Mobile: Horizontal list layout */}
              <div className="md:hidden space-y-0 divide-y divide-zinc-200">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/articles/${article.slug}`}
                    className="flex gap-4 py-6 group"
                    data-testid={`mobile-article-${article.slug}`}
                  >
                    {/* Portrait Thumbnail */}
                    <div className="w-32 h-40 flex-shrink-0 overflow-hidden bg-zinc-100">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                      <span className="font-accent text-xs font-bold uppercase tracking-[0.15em] text-zinc-900 mb-2">
                        {article.category}
                      </span>
                      <h3 className="font-serif text-lg font-bold leading-tight mb-3 group-hover:text-[#FF66C4] transition-colors">
                        {article.title}
                      </h3>
                      <p className="font-accent text-xs uppercase tracking-[0.1em] text-zinc-500 mb-1">
                        By {article.author}
                      </p>
                      <p className="text-sm text-zinc-400">
                        {formatDate(article.created_at)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Desktop: Grid layout */}
              <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/articles/${article.slug}`}
                    className="group block"
                    data-testid={`desktop-article-${article.slug}`}
                  >
                    {/* Portrait Image */}
                    <div className="aspect-[3/4] overflow-hidden bg-zinc-100 mb-4">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    {/* Content */}
                    <div>
                      <span className="font-accent text-sm font-bold uppercase tracking-[0.15em] text-zinc-900 mb-2 block">
                        {article.category}
                      </span>
                      <h3 className="font-serif text-xl font-bold leading-tight mb-3 group-hover:text-[#FF66C4] transition-colors">
                        {article.title}
                      </h3>
                      <p className="font-accent text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1">
                        By {article.author}
                      </p>
                      <p className="text-sm text-zinc-400">
                        {formatDate(article.created_at)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <p className="text-xl text-zinc-500">No articles found.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
